import { useTranslation } from 'react-i18next';
import { FilterPill, Input } from '@/components/ui';
import type { CoverageMatrixVM, MatrixFilter } from './useCoverageMatrix';
import styles from './RoleMatrix.module.css';

type LegendKey = 'linked' | 'uncovered' | 'stale';

// EAA-1846: the swatch modifier used to be built as
// `rc-matrix-legend-swatch--${item.key}`. CSS-Module names are hashed at build
// time, so the tone has to be looked up explicitly rather than interpolated.
const LEGEND_SWATCH_CLASS: Record<LegendKey, string> = {
    linked: styles.legendSwatchLinked,
    uncovered: styles.legendSwatchUncovered,
    stale: styles.legendSwatchStale,
};

/**
 * EAA-1817 (frame 55a): the matrix's own toolbar — search, the three
 * mutually-exclusive filter pills plus the independent "hide not-ready apps"
 * toggle, the colour legend, and the "Hiển thị R × C / R × C" readout.
 *
 * The legend is not decoration. Frame 55a's cells carry meaning in colour and
 * shape alone (green chip / dashed box / struck-through red chip), and a grid
 * of 96 of them has no column header to name the state — the legend is the only
 * place the mapping is written down, which is also why it stays visible rather
 * than hiding behind a tooltip.
 *
 * The readout prints the FILTERED dimensions over the total ones, so it doubles
 * as the answer to "did my filter actually do anything" — the pills' own counts
 * describe the whole grid on purpose (see `useCoverageMatrix`).
 */
export function RoleMatrixToolbar({ vm }: { vm: CoverageMatrixVM }) {
    const { t } = useTranslation();

    const filterPills: { key: MatrixFilter; label: string; count?: number }[] = [
        { key: 'all', label: t('console.roleCoverage.matrixView.filters.all') },
        {
            key: 'uncovered',
            label: t('console.roleCoverage.matrixView.filters.uncovered'),
            count: vm.tallies.uncoveredCount,
        },
        {
            key: 'paused',
            label: t('console.roleCoverage.matrixView.filters.paused'),
            count: vm.tallies.pausedCount,
        },
    ];

    const legend: { key: LegendKey; label: string }[] = [
        { key: 'linked', label: t('console.roleCoverage.matrixView.legend.linked') },
        { key: 'uncovered', label: t('console.roleCoverage.matrixView.legend.uncovered') },
        { key: 'stale', label: t('console.roleCoverage.matrixView.legend.stale') },
    ];

    return (
        <div className={styles.toolbar}>
            <Input
                className={styles.toolbarSearch}
                placeholder={t('console.roleCoverage.matrixView.searchPlaceholder')}
                value={vm.search}
                onChange={(event) => vm.setSearch(event.target.value)}
                aria-label={t('console.roleCoverage.matrixView.searchAriaLabel')}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            <div
                className={styles.toolbarPills}
                role="group"
                aria-label={t('console.roleCoverage.matrixView.filtersAriaLabel')}
            >
                {filterPills.map((pill) => (
                    <FilterPill
                        key={pill.key}
                        selected={vm.filter === pill.key}
                        count={pill.count}
                        onClick={() => vm.setFilter(pill.key)}
                    >
                        {pill.label}
                    </FilterPill>
                ))}

                {/* Independent of the three above — it hides COLUMNS, they filter
                    ROWS, so it stays pressed across a change of filter. */}
                <FilterPill
                    tone="toggle"
                    selected={vm.hideNotReady}
                    count={vm.notReadyCount > 0 ? vm.notReadyCount : undefined}
                    onClick={() => vm.setHideNotReady(!vm.hideNotReady)}
                >
                    {t('console.roleCoverage.matrixView.filters.hideNotReady')}
                </FilterPill>
            </div>

            <ul className={styles.toolbarLegend}>
                {legend.map((item) => (
                    <li key={item.key} className={styles.legendItem}>
                        <span
                            className={`${styles.legendSwatch} ${LEGEND_SWATCH_CLASS[item.key]}`}
                            aria-hidden="true"
                        />
                        {item.label}
                    </li>
                ))}
            </ul>

            <p className={styles.toolbarCount}>
                {t('console.roleCoverage.matrixView.showing', {
                    rows: vm.visibleRows.length,
                    columns: vm.visibleColumns.length,
                    totalRows: vm.allRows.length,
                    totalColumns: vm.allColumns.length,
                })}
            </p>
        </div>
    );
}

export default RoleMatrixToolbar;
